import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import apiService from '../../services/apiService';
import { useAlert } from '../../context/AlertContext';
import {
    Search,
    Mail,
    Phone,
    User,
    Calendar,
    Eye,
    X
} from 'lucide-react';
import useLockBodyScroll from '../../utils/useLockBodyScroll';

const ViewRequestModal = ({ isOpen, onClose, request, onUpdateStatus }) => {
    useLockBodyScroll(isOpen);
    const [remark, setRemark] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (request) {
            setRemark(request.admin_remark || '');
        }
    }, [request]);

    if (!isOpen || !request) return null;

    const handleUpdate = async (status) => {
        setLoading(true);
        try {
            await onUpdateStatus(request.id, status, remark);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-[#FF5C00] font-bold text-lg border border-orange-200">
                            <Mail size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">Contact Request</h3>
                            <p className="text-sm text-gray-500">From {request.name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Name</p>
                            <p className="font-bold text-gray-900 flex items-center gap-2"><User size={14} /> {request.name}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Email</p>
                            <p className="font-bold text-gray-900 flex items-center gap-2"><Mail size={14} /> {request.email}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Phone</p>
                            <p className="font-bold text-gray-900 flex items-center gap-2"><Phone size={14} /> {request.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Date</p>
                            <p className="font-bold text-gray-900 flex items-center gap-2"><Calendar size={14} /> {new Date(request.created_at).toLocaleString()}</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl">
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Subject</p>
                        <p className="font-bold text-gray-900">{request.subject}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#FF5C00]">
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Message</p>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{request.message}</p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Admin Remark</label>
                        <textarea
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF5C00] outline-none transition-all resize-none font-medium"
                            rows="3"
                            placeholder="Add your notes here..."
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                        ></textarea>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap justify-end gap-3">
                    <button
                        onClick={() => handleUpdate('pending')}
                        disabled={loading}
                        className="px-4 py-2 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition-all text-xs uppercase tracking-widest"
                    >
                        Mark Pending
                    </button>
                    <button
                        onClick={() => handleUpdate('read')}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-100 transition-all text-xs uppercase tracking-widest border border-blue-100"
                    >
                        Mark Read
                    </button>
                    <button
                        onClick={() => handleUpdate('replied')}
                        disabled={loading}
                        className="px-4 py-2 bg-green-50 text-green-600 font-bold rounded-xl hover:bg-green-100 transition-all text-xs uppercase tracking-widest border border-green-100"
                    >
                        Mark Replied
                    </button>
                    <button
                        onClick={() => handleUpdate('closed')}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all text-xs uppercase tracking-widest"
                    >
                        Close Ticket
                    </button>
                </div>
            </div>
        </div>
    );
};

const ContactRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { showAlert } = useAlert();

    const fetchRequests = React.useCallback(async () => {
        setLoading(true);
        try {
            const filters = {};
            if (fromDate) filters.from = fromDate;
            if (toDate) filters.to = toDate;

            const response = await apiService.getAdminContactRequests(filters);
            if (response.success) {
                setRequests(response.data);
            }
        } catch (error) {
            showAlert('Failed to fetch contact requests', 'error');
        } finally {
            setLoading(false);
        }
    }, [showAlert, fromDate, toDate]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleUpdateStatus = async (id, status, remark) => {
        try {
            const response = await apiService.updateContactRequest(id, status, remark);
            if (response.success) {
                showAlert('Request updated successfully', 'success');
                fetchRequests();
            }
        } catch (error) {
            showAlert(error.message || 'Failed to update request', 'error');
        }
    };

    const getStatusStyle = (status) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            case 'read': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'replied': return 'bg-green-100 text-green-700 border-green-200';
            case 'closed': return 'bg-gray-100 text-gray-700 border-gray-200';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const filteredRequests = requests.filter(req => {
        const matchesSearch =
            req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.subject.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'All' || req.status === filterStatus.toLowerCase();
        return matchesSearch && matchesFilter;
    });

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Contact Requests</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage inquiries from your landing page</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Total Inquiries</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{requests.length}</h3>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-yellow-600 text-xs font-semibold uppercase tracking-wider">Pending</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{requests.filter(r => r.status === 'pending').length}</h3>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-blue-600 text-xs font-semibold uppercase tracking-wider">Read</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{requests.filter(r => r.status === 'read').length}</h3>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-green-600 text-xs font-semibold uppercase tracking-wider">Replied</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{requests.filter(r => r.status === 'replied').length}</h3>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col lg:flex-row justify-between gap-4 items-center">
                    <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                            />
                        </div>
                        <span className="text-gray-300 text-xs font-black">to</span>
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                            />
                        </div>

                        <button
                            onClick={fetchRequests}
                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-all"
                        >
                            Filter
                        </button>

                        <div className="h-8 w-px bg-gray-100 hidden lg:block"></div>

                        <select
                            className="px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5C00] bg-white text-gray-700 font-bold text-xs"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="All">All Status</option>
                            <option value="Pending">Pending</option>
                            <option value="Read">Read</option>
                            <option value="Replied">Replied</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>

                    <div className="relative w-full lg:w-80">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search name, subject..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5C00] transition-all text-xs font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <>
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs uppercase tracking-wider">
                                        <th className="px-6 py-4 font-semibold">User Info</th>
                                        <th className="px-6 py-4 font-semibold">Subject</th>
                                        <th className="px-6 py-4 font-semibold">Date</th>
                                        <th className="px-6 py-4 font-semibold">Status</th>
                                        <th className="px-6 py-4 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-400">Loading requests...</td>
                                        </tr>
                                    ) : filteredRequests.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-400">No requests found</td>
                                        </tr>
                                    ) : filteredRequests.map((req) => (
                                        <tr key={req.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-gray-900">{req.name}</span>
                                                    <span className="text-xs text-gray-500">{req.email}</span>
                                                    {req.phone && <span className="text-[10px] text-gray-400">{req.phone}</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm text-gray-700 font-medium truncate max-w-xs">{req.subject}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-xs text-gray-500 font-medium">
                                                    {new Date(req.created_at).toLocaleDateString()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(req.status)}`}>
                                                    {req.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => { setSelectedRequest(req); setIsModalOpen(true); }}
                                                    className="p-2 text-gray-400 hover:text-[#FF5C00] hover:bg-orange-50 rounded-xl transition-all"
                                                    title="View & Action"
                                                >
                                                    <Eye size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className="lg:hidden divide-y divide-gray-100">
                            {loading ? (
                                <div className="p-12 text-center text-gray-400">Loading requests...</div>
                            ) : filteredRequests.length === 0 ? (
                                <div className="p-12 text-center text-gray-400">No requests found</div>
                            ) : filteredRequests.map((req) => (
                                <div key={req.id} className="p-4 flex flex-col gap-3 hover:bg-gray-50/50 transition-colors">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-sm font-bold text-gray-900">{req.name}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{req.email}</div>
                                            {req.phone && <div className="text-[10px] text-gray-400 mt-0.5">{req.phone}</div>}
                                        </div>
                                        <div className="text-right flex flex-col items-end gap-1">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(req.status)}`}>
                                                {req.status}
                                            </span>
                                            <span className="text-[10px] text-gray-400 font-bold">
                                                {new Date(req.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Subject</span>
                                        <p className="text-xs text-gray-700 font-medium truncate">{req.subject}</p>
                                    </div>

                                    <div className="pt-2 border-t border-gray-50">
                                        <button
                                            onClick={() => { setSelectedRequest(req); setIsModalOpen(true); }}
                                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-xl transition-all text-xs font-black uppercase tracking-widest"
                                        >
                                            <Eye size={16} />
                                            View & Action
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                </div>
            </div>

            <ViewRequestModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                request={selectedRequest}
                onUpdateStatus={handleUpdateStatus}
            />
        </Layout>
    );
};

export default ContactRequests;
